import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/supabase/client'
import { useLoginModal } from '@/contexts/LoginModalContext'
import { logError } from '@/lib/sentry'

// 인증 상태 타입
type AuthStatus = 'LOADING' | 'ERROR' | 'SUCCESS' | 'CHECKING_MEMBER';

// 인증 오류 정보 타입
interface AuthError {
  code?: string;
  message: string;
}

export default function AuthCallback() {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { user, signInWithKakao, setIsAuthenticating } = useAuth()
  const { openLoginModal, closeLoginModal, setShowSignupForm } = useLoginModal()
  const [authStatus, setAuthStatus] = useState<AuthStatus>('LOADING')
  const [error, setError] = useState<AuthError | null>(null)
  const isAuthenticatingClearedRef = useRef(false) // 중복 해제 방지

  // 인증 파라미터 처리 로직
  const handleAuthParams = useCallback(async () => {
    try {
      // 오류 파라미터 확인
      const errorParam = searchParams.get('error') || location.hash.includes('error=')
      const errorCode = searchParams.get('error_code')
      const errorDescription = searchParams.get('error_description')
      
      if (errorParam) {
        setError({
          code: errorCode || 'AUTH_ERROR',
          message: errorDescription || '인증 중 오류가 발생했습니다. Supabase와 카카오 연동에 문제가 있습니다.'
        })
        setAuthStatus('ERROR')
        return
      }
      
      // 해시 파라미터 처리
      const hashParams = location.hash
      let accessToken, refreshToken
      
      if (hashParams && hashParams.length > 1) {
        const processHashParams = () => {
          // 해시 처리
          let hashWithoutPrefix = hashParams.substring(1)
          
          // 복잡한 해시 구조 처리
          if (hashWithoutPrefix.includes('#')) {
            hashWithoutPrefix = hashWithoutPrefix.replace(/#/g, '&')
          }
          
          try {
            const hashParamsObj = new URLSearchParams(hashWithoutPrefix)
            accessToken = hashParamsObj.get('access_token')
            refreshToken = hashParamsObj.get('refresh_token')
            
            // 대체 파라미터 이름 시도
            if (!accessToken) accessToken = hashParamsObj.get('token')
            if (!refreshToken) refreshToken = hashParamsObj.get('refresh')
            
            return { accessToken, refreshToken }
          } catch (error) {
            logError("해시 파라미터 파싱 실패:", error)
            return { accessToken: null, refreshToken: null }
          }
        }
        
        const { accessToken: hashToken, refreshToken: hashRefresh } = processHashParams()
        accessToken = hashToken
        refreshToken = hashRefresh
      }
      
      // 쿼리 파라미터 처리
      if (!accessToken || !refreshToken) {
        accessToken = searchParams.get('access_token')
        refreshToken = searchParams.get('refresh_token')
        
        // 대체 파라미터명 시도
        if (!accessToken) accessToken = searchParams.get('token')
        if (!refreshToken) refreshToken = searchParams.get('refresh')
      }
      
      // 카카오 토큰 처리
      const kakaoAccessToken = searchParams.get('token') || searchParams.get('provider_token')
      
      if ((kakaoAccessToken) && (!accessToken || !refreshToken)) {
        try {
          // 카카오 토큰으로 로그인 시도
          const { error } = await supabase.auth.signInWithIdToken({
            provider: 'kakao',
            token: kakaoAccessToken
          })
          
          if (error) {
            throw error
          }
        } catch (error) {
          logError("카카오 토큰 처리 오류:", error)
        }
      }
      
      // 현재 세션 확인
      const { data: { session } } = await supabase.auth.getSession()
      
      if (session?.access_token && session?.refresh_token) {
        // 세션이 이미 설정됨
        setAuthStatus('CHECKING_MEMBER')
        return
      }
      
      // 액세스 코드 처리
      const code = searchParams.get('code')
      if (code) {
        // code는 Supabase가 내부적으로 처리
        setAuthStatus('CHECKING_MEMBER')
        return
      }
      
      // 토큰이 없는 경우
      if (!accessToken || !refreshToken) {
        setAuthStatus('CHECKING_MEMBER')
        return
      }
      
      // 세션 설정
      const { error } = await supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken
      })
      
      if (error) {
        setError({
          code: 'SESSION_ERROR',
          message: '인증 처리 중 오류가 발생했습니다'
        })
        setAuthStatus('ERROR')
        return
      }
      
      // URL에서 해시 및 불필요한 쿼리 파라미터 제거
      const path = location.pathname
      const cleanUrl = `${path}?from=${searchParams.get('from') || ''}`
      window.history.replaceState(null, '', cleanUrl)
      
      setAuthStatus('CHECKING_MEMBER')
    } catch (error) {
      setError({
        code: 'PROCESS_ERROR',
        message: '인증 처리 중 오류가 발생했습니다. 관리자에게 문의하세요.'
      })
      setAuthStatus('ERROR')
    }
  }, [location, searchParams])

  // isAuthenticating 플래그 해제 헬퍼 (중복 호출 방지)
  const clearAuthenticating = useCallback(() => {
    if (!isAuthenticatingClearedRef.current) {
      isAuthenticatingClearedRef.current = true
      setIsAuthenticating(false)
    }
  }, [setIsAuthenticating])

  // 사용자 멤버십 상태 확인
  const checkMemberStatus = useCallback(async () => {
    if (!user) {
      console.log('AuthCallback: checkMemberStatus - user is null');
      return
    }
    console.log('AuthCallback: checkMemberStatus - user object:', user);

    try {
      console.log('AuthCallback: checkMemberStatus - Calling Supabase to check member status for user_id:', user.id);
      const { data: memberData, error } = await supabase
        .from('member')
        .select('nickname')
        .eq('user_id', user.id)
        .single()

      console.log('AuthCallback: checkMemberStatus - Supabase response:', { memberData, error });

      const from = searchParams.get('from') || ''

      if (error && error.code === 'PGRST116') {
        console.log('AuthCallback: checkMemberStatus - New member detected.');
        // 신규 회원 - 회원가입 폼 표시 (마이그레이션 완료 후 Home으로 이동)
        clearAuthenticating() // 로그인 처리 완료 플래그 해제
        setShowSignupForm(true);
        // 회원가입 완료 핸들러를 변경하여 마이그레이션 후 리다이렉션
        const redirectPath = from ? `/${from}` : '/';
        
        // LoginModal에 onSignupComplete 콜백 전달
        openLoginModal({
          onSignupComplete: () => {
            console.log('AuthCallback: 회원가입 및 마이그레이션 완료, 홈으로 이동');
            // 회원가입 및 마이그레이션이 완료된 후 리다이렉션
            navigate(redirectPath);
          }
        });
        return;
      } else if (error) {
        logError('AuthCallback: checkMemberStatus - Supabase error (not PGRST116):', error);
        clearAuthenticating() // 로그인 처리 완료 플래그 해제
        // PGRST116 이외의 다른 에러 처리
        setError({
          code: error.code || 'DB_CHECK_ERROR',
          message: `회원 정보 확인 중 오류가 발생했습니다: ${error.message}`
        });
        setAuthStatus('ERROR');
        return;
      } else if (!error && memberData) {
        console.log('AuthCallback: checkMemberStatus - Existing member detected. Nickname:', memberData.nickname);
        clearAuthenticating() // 로그인 처리 완료 플래그 해제
        // 기존 회원 - 리디렉션
        closeLoginModal()
        navigate(from ? `/${from}` : '/')
      } else {
        // 예상치 못한 경우 (error도 없고 memberData도 없는 경우)
        console.warn('AuthCallback: checkMemberStatus - Unexpected state: No error and no memberData.');
        clearAuthenticating() // 로그인 처리 완료 플래그 해제
        setError({
          code: 'UNEXPECTED_STATE',
          message: '회원 정보를 확인할 수 없습니다. 다시 시도해주세요.'
        });
        setAuthStatus('ERROR');
        return;
      }
      
      setAuthStatus('SUCCESS')
    } catch (error) {
      logError('AuthCallback: checkMemberStatus - Exception caught:', error)
      clearAuthenticating() // 로그인 처리 완료 플래그 해제
      // navigate('/') // 에러 발생 시 홈으로 바로 이동하는 대신 에러 화면을 보여주도록 변경
      setError({
        code: 'CHECK_MEMBER_EXCEPTION',
        message: '회원 상태 확인 중 예외가 발생했습니다. 관리자에게 문의하세요.'
      });
      setAuthStatus('ERROR');
    }
  }, [user, searchParams, navigate, openLoginModal, closeLoginModal, setShowSignupForm, clearAuthenticating])

  // 인증 파라미터 처리
  useEffect(() => {
    handleAuthParams()
  }, [handleAuthParams])

  // 멤버십 상태 확인
  useEffect(() => {
    // 인증 파라미터 처리가 완료되면 멤버십 확인
    if (authStatus === 'CHECKING_MEMBER') {
      console.log('AuthCallback: useEffect[authStatus, user] - authStatus is CHECKING_MEMBER');
      if (user) {
        console.log('AuthCallback: useEffect[authStatus, user] - user object exists, calling checkMemberStatus');
        checkMemberStatus()
      } else {
        console.log('AuthCallback: useEffect[authStatus, user] - user object is null, setting timeout to navigate home.');
        // 3초 후에도 사용자 정보가 없으면 홈으로 리디렉션
        const timer = setTimeout(() => {
          console.log('AuthCallback: useEffect[authStatus, user] - Timeout reached, navigating to home.');
          clearAuthenticating() // 로그인 처리 완료 플래그 해제
          navigate('/')
        }, 3000)
        return () => clearTimeout(timer)
      }
    }
  }, [authStatus, user, navigate, checkMemberStatus, clearAuthenticating])

  // 인증 오류 화면 렌더링
  if (authStatus === 'ERROR' && error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-4">
        <h2 className="text-xl font-bold mb-4">인증 오류</h2>
        <p className="text-red-500 mb-4">{error.message}</p>
        <div className="flex flex-col sm:flex-row gap-4 mb-4">
          <button 
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            홈으로 돌아가기
          </button>
          <button 
            onClick={() => signInWithKakao()}
            className="px-4 py-2 bg-yellow-500 text-white rounded hover:bg-yellow-600"
          >
            카카오 로그인 재시도
          </button>
        </div>
        <div className="mt-4 text-sm text-gray-500 max-w-md text-center">
          <p>문제가 계속되면 다음을 확인해보세요:</p>
          <ul className="list-disc list-inside mt-2 text-left">
            <li>브라우저 쿠키 및 캐시 삭제 후 다시 시도</li>
            <li>다른 브라우저로 시도</li>
            <li>문제가 지속되면 고객센터에 문의해주세요</li>
          </ul>
        </div>
        <div className="mt-6 text-xs text-gray-400">
          오류 코드: {error.code || 'AUTH_CALLBACK_ERROR'}
        </div>
      </div>
    )
  }

  // 로딩 화면 렌더링
  return (
    <div className="flex flex-col items-center justify-center min-h-[50vh] p-4">
      <h2 className="text-xl font-bold mb-4">로그인 처리 중...</h2>
      <p className="text-gray-500 mb-4">잠시만 기다려주세요...</p>
      <div className="w-16 h-16 border-t-4 border-blue-500 border-solid rounded-full animate-spin"></div>
    </div>
  )
} 